/**
 * What to call a person on screen.
 *
 * The mirror of `backend/src/utils/display_name.py`, and it must stay one:
 * the same person showing as "Gino Volpi" on a page and "gino.volpi" in a
 * panel is exactly the inconsistency this replaces. Fifteen places derived
 * this independently and no two agreed.
 *
 * The order is deliberate:
 *
 * 1. Their name, when they have one. A name is what a person is called; a
 *    handle is how you address them. Slack shows "Gino Volpi", not "@gino".
 * 2. Their handle, when they have no name. This is the improvement — these
 *    sites used to fall back to a mangled email local part.
 * 3. The email local part, cleaned up, as the last resort it always was.
 *
 * Formats only. Fetching a handle is the caller's job (see `useMyHandle`),
 * because a display helper that quietly makes a request is a display helper
 * that fires one on every render.
 *
 * Not every `split('@')[0]` belongs here: an export filename
 * (`QueryHistoryCard`) and a channel identifier are not display names, and
 * seeding a stored `name` at signup is persistent data rather than
 * presentation.
 */

const SEPARATORS = /[._\-+]+/;

export interface DisplayNameInput {
  name?: string | null;
  email?: string | null;
  handle?: string | null;
  fallback?: string;
}

function fromEmail(email?: string | null): string {
  if (!email || !email.includes('@')) return '';
  const local = email.split('@')[0].split('+')[0];
  const words = local.split(SEPARATORS).filter(Boolean);
  if (words.length === 0) return '';
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function displayName(
  { name, email, handle, fallback = 'User' }: DisplayNameInput = {},
): string {
  if (name && name.trim()) return name.trim();

  if (handle && handle.trim()) {
    const cleaned = handle.trim().replace(/^@+/, '');
    if (cleaned) return `@${cleaned}`;
  }

  return fromEmail(email) || fallback;
}

/**
 * Just the first word, for greetings ("Hola, Gino").
 *
 * A handle has no first word — it is one token by construction — so it comes
 * back whole rather than chopped at a separator that means nothing.
 */
export function firstName(
  { name, email, handle, fallback = '' }: DisplayNameInput = {},
): string {
  const full = displayName({ name, email, handle, fallback });
  if (full.startsWith('@')) return full;
  return full ? full.split(' ')[0] : fallback;
}

/** Initials for an avatar placeholder, from whatever the rules above yield. */
export function initials(
  { name, email, handle, fallback = 'U' }: DisplayNameInput = {},
): string {
  const full = displayName({ name, email, handle, fallback: '' });
  if (!full) return fallback;
  if (full.startsWith('@')) return full.charAt(1).toUpperCase() || fallback;

  const parts = full.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
}

export default displayName;
